import React from 'react';
import { Lightbulb, Beaker, Target, TrendingUp, CheckCircle, Users, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const Research = () => {
  const focusAreas = [
    {
      icon: <Beaker className="h-8 w-8 text-blue-500" />,
      title: "Product Development",
      description: "Creating innovative food products that meet market demands and nutritional requirements"
    },
    {
      icon: <Target className="h-8 w-8 text-green-500" />,
      title: "Food Safety",
      description: "Advancing food safety protocols and testing methods for superior quality assurance"
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-purple-500" />,
      title: "Sustainability",
      description: "Developing eco-friendly production methods and sustainable food packaging solutions"
    },
    {
      icon: <Award className="h-8 w-8 text-orange-500" />,
      title: "Nutritional Enhancement",
      description: "Improving nutritional profiles while maintaining taste and texture preferences"
    }
  ];

  const innovations = [
    "Plant-based meat alternatives with authentic taste and texture",
    "Traditional dumplings with extended shelf life",
    "Premium fish otah with enhanced nutritional value",
    "Therapeutic diet innovations for specialized healthcare needs",
    "Sustainable packaging solutions for frozen food products",
    "Automated production processes for improved efficiency"
  ];

  const rndCapabilities = [
    "Product formulation and recipe development",
    "Nutritional analysis and optimization",
    "Shelf life testing and preservation methods",
    "Taste testing and consumer preference analysis",
    "Food safety and quality assurance protocols",
    "Production scalability assessment"
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-red-600 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Research & Development</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Driving innovation in food manufacturing through dedicated research, 
              continuous development, and cutting-edge solutions for tomorrow's food industry.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Innovation at the Heart of Our Operations
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  Our dedicated in-house Research & Development department is the driving force 
                  behind our continuous innovation in food manufacturing. We focus on developing, 
                  testing, and enhancing food products to achieve excellence in taste, safety, 
                  nutrition, shelf life, and sustainability.
                </p>
                <p>
                  With a philosophy of continuous menu evolution and product improvement, our R&D team 
                  works tirelessly to stay ahead of market trends while meeting the changing needs 
                  of our customers and the broader food industry.
                </p>
              </div>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1651525670099-f828fb5478a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxmb29kJTIwbWFudWZhY3R1cmluZ3xlbnwwfHx8fDE3NjAwMDM3NzN8MA&ixlib=rb-4.1.0&q=85"
                alt="R&D Laboratory"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Research Focus Areas
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our R&D efforts span multiple disciplines, ensuring comprehensive innovation 
              across all aspects of food manufacturing and product development.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {focusAreas.map((area, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4">
                    {area.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{area.title}</h3>
                  <p className="text-gray-600">{area.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Innovations */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Recent Innovations
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Our R&D team has successfully introduced several innovative products 
                that showcase our commitment to culinary excellence and market leadership.
              </p>

              <div className="space-y-4">
                {innovations.map((innovation, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{innovation}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1622021142947-da7dedc7c39a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxjb21tZXJjaWFsJTIwa2l0Y2hlbnxlbnwwfHx8fDE3NjAwMDM3ODB8MA&ixlib=rb-4.1.0&q=85"
                alt="Product Innovation"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* R&D Capabilities */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              R&D Capabilities
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive research and development capabilities that support 
              every aspect of food product innovation and improvement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rndCapabilities.map((capability, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <Lightbulb className="h-6 w-6 text-yellow-500" />
                    <h3 className="font-semibold text-gray-900">{capability}</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    Advanced methodologies and state-of-the-art equipment ensure precise results
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy & Approach */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our R&D Philosophy
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center">
              <CardContent className="p-6">
                <Users className="h-12 w-12 text-blue-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-3">Continuous Evolution</h3>
                <p className="text-gray-600">
                  We believe in constantly evolving our menu and product offerings to stay 
                  relevant and exceed customer expectations.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="p-6">
                <Target className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-3">Market-Driven Innovation</h3>
                <p className="text-gray-600">
                  Our innovations are guided by market insights and customer feedback, 
                  ensuring practical and commercially viable solutions.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="p-6">
                <Award className="h-12 w-12 text-purple-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-3">Quality Excellence</h3>
                <p className="text-gray-600">
                  Every innovation must meet our stringent quality standards while 
                  delivering enhanced value to our customers.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Partner with Our R&D Team
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Interested in collaborating on innovative food products or need custom R&D services? 
            Our team is ready to help bring your concepts to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors">
              Discuss R&D Partnership
            </button>
            <button className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-3 rounded-lg font-semibold transition-colors">
              View Our Innovations
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Research;